import { useCallback, useEffect, useRef, useState } from "react";
import {
  type BootstrapResponse,
  type BuildGraphResponse,
  type DiscoveryResult,
  type GeneratedFile,
  type PushFilesResponse,
  type RepoInfo,
  bootstrapProject,
  buildGraph,
  getGeneratedFiles,
  getGitAuthStatus,
  getOAuthUrl,
  listBranches,
  listRepos,
  pollDiscovery,
  pushFiles,
  startDiscovery,
} from "@/api/gitAuth";

export type WizardStep =
  | "connect"
  | "select-repo"
  | "select-branch"
  | "discovering"
  | "configuring"
  | "ready";

export type ConfigureSubStep =
  | "building-graph"
  | "graph-done"
  | "pushing"
  | "done";

export interface WizardState {
  step: WizardStep;
  provider: string | null;
  providerUsername: string | null;
  repos: RepoInfo[];
  selectedRepo: RepoInfo | null;
  branches: string[];
  selectedBranch: string;
  discoveryJobId: string | null;
  discoveryStatus: string;
  discoveryMessage: string;
  discoveryResult: DiscoveryResult | null;
  bootstrapResult: BootstrapResponse | null;
  generatedFiles: GeneratedFile[];
  pushResult: PushFilesResponse | null;
  configureSubStep: ConfigureSubStep;
  graphResult: BuildGraphResponse | null;
  error: string | null;
  loading: boolean;
}

const INITIAL_STATE: WizardState = {
  step: "connect",
  provider: null,
  providerUsername: null,
  repos: [],
  selectedRepo: null,
  branches: [],
  selectedBranch: "",
  discoveryJobId: null,
  discoveryStatus: "",
  discoveryMessage: "",
  discoveryResult: null,
  bootstrapResult: null,
  generatedFiles: [],
  pushResult: null,
  configureSubStep: "building-graph",
  graphResult: null,
  error: null,
  loading: false,
};

export function useWizard() {
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const checkExistingConnection = useCallback(async () => {
    try {
      const status = await getGitAuthStatus();
      if (status.providers.length > 0) {
        const connected = status.providers[0];
        setState((s) => ({
          ...s,
          step: "select-repo",
          provider: connected.provider,
          providerUsername: connected.username,
        }));
      }
    } catch {
      // No connection yet — stay on connect step
    }
  }, []);

  const connectProvider = useCallback(async (provider: string) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const redirectUri = `${window.location.origin}/auth/callback`;
      const { url } = await getOAuthUrl(provider, redirectUri);
      window.location.href = url;
    } catch {
      setState((s) => ({
        ...s,
        loading: false,
        error: `Failed to start ${provider} OAuth`,
      }));
    }
  }, []);

  const loadRepos = useCallback(async (provider: string, search = "") => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { repos } = await listRepos(provider, search);
      setState((s) => ({
        ...s,
        repos,
        loading: false,
        step: "select-repo",
        provider,
      }));
    } catch (err) {
      const is401 = err instanceof Error && err.message.includes("500");
      setState((s) => ({
        ...s,
        loading: false,
        step: is401 ? "connect" : "select-repo",
        error: is401
          ? "Token expired — please reconnect your Git provider."
          : "Failed to load repositories",
      }));
    }
  }, []);

  const selectRepo = useCallback(
    async (repo: RepoInfo) => {
      if (!state.provider) return;
      setState((s) => ({
        ...s,
        selectedRepo: repo,
        loading: true,
        error: null,
      }));

      try {
        const { branches, default_branch } = await listBranches(
          state.provider,
          repo.provider_repo_id,
        );
        setState((s) => ({
          ...s,
          step: "select-branch",
          branches,
          selectedBranch: default_branch,
          loading: false,
        }));
      } catch {
        // Fallback: skip branch selection, use repo's default_branch
        setState((s) => ({
          ...s,
          step: "select-branch",
          branches: [repo.default_branch],
          selectedBranch: repo.default_branch,
          loading: false,
        }));
      }
    },
    [state.provider],
  );

  const startDiscoveryWithBranch = useCallback(
    async (branch: string) => {
      if (!state.provider || !state.selectedRepo) return;
      const repo = state.selectedRepo;

      setState((s) => ({
        ...s,
        selectedBranch: branch,
        step: "discovering",
        discoveryStatus: "cloning",
        discoveryMessage: "Starting discovery...",
        error: null,
      }));

      try {
        const job = await startDiscovery(
          state.provider,
          repo.provider_repo_id,
          repo.clone_url,
          branch,
          repo.full_name,
        );

        const currentProvider = state.provider;

        setState((s) => ({
          ...s,
          discoveryJobId: job.job_id,
          discoveryStatus: job.status,
          discoveryMessage: job.message,
        }));

        pollRef.current = setInterval(async () => {
          try {
            const poll = await pollDiscovery(job.job_id);
            setState((s) => ({
              ...s,
              discoveryStatus: poll.status,
              discoveryMessage: poll.message,
            }));

            if (poll.status === "complete" && poll.result) {
              stopPolling();
              setState((s) => ({ ...s, discoveryResult: poll.result }));

              const bootstrap = await bootstrapProject(
                currentProvider!,
                job.job_id,
                repo.name,
              );
              const generated = await getGeneratedFiles(job.job_id);

              setState((s) => ({
                ...s,
                step: "configuring",
                configureSubStep: "building-graph",
                loading: true,
                bootstrapResult: bootstrap,
                generatedFiles: generated.files,
              }));

              try {
                const graphRes = await buildGraph(job.job_id);
                setState((s) => ({
                  ...s,
                  graphResult: graphRes,
                  configureSubStep: "graph-done",
                  loading: false,
                }));
              } catch {
                setState((s) => ({
                  ...s,
                  configureSubStep: "graph-done",
                  loading: false,
                  error: "Graph build failed — you can still push files.",
                }));
              }
            } else if (poll.status === "error") {
              stopPolling();
              setState((s) => ({
                ...s,
                error: poll.error ?? "Discovery failed",
              }));
            }
          } catch {
            stopPolling();
            setState((s) => ({
              ...s,
              error: "Failed to poll discovery status",
            }));
          }
        }, 2000);
      } catch {
        setState((s) => ({
          ...s,
          error: "Failed to start discovery",
          step: "select-branch",
        }));
      }
    },
    [state.provider, state.selectedRepo, stopPolling],
  );

  const pushToRepo = useCallback(async () => {
    if (!state.provider || !state.discoveryJobId) return;
    setState((s) => ({
      ...s,
      loading: true,
      error: null,
      configureSubStep: "pushing",
    }));
    try {
      const result = await pushFiles(state.provider, state.discoveryJobId);
      setState((s) => ({
        ...s,
        pushResult: result,
        configureSubStep: "done",
        step: "ready",
        loading: false,
      }));
    } catch {
      setState((s) => ({
        ...s,
        loading: false,
        configureSubStep: "graph-done",
        error: "Failed to push files to repository",
      }));
    }
  }, [state.provider, state.discoveryJobId]);

  const skipPush = useCallback(() => {
    setState((s) => ({
      ...s,
      step: "ready",
    }));
  }, []);

  const retry = useCallback(() => {
    stopPolling();
    setState((s) => ({
      ...s,
      error: null,
      discoveryJobId: null,
      discoveryStatus: "",
      discoveryMessage: "",
      discoveryResult: null,
      step: s.provider ? "select-repo" : "connect",
    }));
  }, [stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setState(INITIAL_STATE);
  }, [stopPolling]);

  return {
    state,
    checkExistingConnection,
    connectProvider,
    loadRepos,
    selectRepo,
    startDiscoveryWithBranch,
    pushToRepo,
    skipPush,
    retry,
    reset,
  };
}
